import React, { useEffect, useRef, useState, useCallback } from "react";
import client, { apiErr } from "../lib/api";
import { Button, Card, Badge } from "./ui";
import { Upload, Save, Loader2, FileSignature, Trash2, Eye } from "lucide-react";

const FIELD_COLORS = {
  runner_name: "#0F62FE", runner_sig: "#0F62FE", runner_name2: "#0F62FE",
  runner_date: "#0F62FE",
  gca_sig: "#DA1E28", gca_name: "#DA1E28", gca_title: "#DA1E28", gca_date: "#DA1E28",
};

export default function SaasSignableSetup({ setMsg }) {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [placements, setPlacements] = useState({});
  const [gca, setGca] = useState({ name: "", title: "" });
  const [viewPage, setViewPage] = useState(1);
  const [pageImg, setPageImg] = useState("");
  const [imgLoading, setImgLoading] = useState(false);
  const [drag, setDrag] = useState(null);
  const fileRef = useRef(null);
  const boxRef = useRef(null);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await client.get("/admin/saas-pdf");
      setMeta(data); setPlacements(data.placements || {});
      setGca({ name: data.gca?.name || "E. George", title: data.gca?.title || "CEO, GCA" });
    } catch (e) { setMsg(apiErr(e)); }
  }, [setMsg]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const loadPage = useCallback(async (n) => {
    if (!meta?.uploaded) return;
    setImgLoading(true);
    try {
      const res = await client.get(`/admin/saas-pdf/page/${n}.png`, { responseType: "blob" });
      setPageImg((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
    } catch (e) { setMsg(apiErr(e)); }
    finally { setImgLoading(false); }
  }, [meta, setMsg]);
  useEffect(() => { if (meta?.uploaded) loadPage(viewPage); }, [meta, viewPage, loadPage]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setBusy(true); setMsg(`Uploading “${file.name}”…`);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await client.post("/admin/saas-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg(`✓ Uploaded ${data.pages}-page SaaS Agreement. Now drag each field onto the correct spot.`);
      await loadMeta();
    } catch (e2) { setMsg(apiErr(e2)); }
    finally { setBusy(false); }
  };

  const savePlacements = async () => {
    setBusy(true); setMsg("Saving field positions…");
    try {
      await client.put("/admin/saas-pdf/placements", { placements, gca_name: gca.name, gca_title: gca.title });
      setMsg("✓ Field positions saved. Use “Preview executed sample” to verify.");
    } catch (e) { setMsg(apiErr(e)); }
    finally { setBusy(false); }
  };

  const removePdf = async () => {
    if (!window.confirm("Remove the uploaded SaaS Agreement PDF? New signings will fall back to the generated copy.")) return;
    try { await client.delete("/admin/saas-pdf"); setPageImg(""); await loadMeta(); setMsg("Removed."); }
    catch (e) { setMsg(apiErr(e)); }
  };

  const preview = async () => {
    try {
      const res = await client.get("/admin/saas-pdf/preview.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data); window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { setMsg(apiErr(e)); }
  };

  // dragging markers
  useEffect(() => {
    if (!drag) return;
    const move = (ev) => {
      const box = boxRef.current; if (!box) return;
      const r = box.getBoundingClientRect();
      const nx = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width));
      const ny = Math.max(0, Math.min(1, (ev.clientY - r.top) / r.height));
      setPlacements((p) => ({ ...p, [drag]: { ...p[drag], x: nx, y: ny } }));
    };
    const up = () => setDrag(null);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, [drag]);

  const labels = meta?.field_labels || {};
  const setField = (k, patch) => setPlacements((p) => ({ ...p, [k]: { ...p[k], ...patch } }));

  return (
    <Card className="space-y-4" data-testid="saas-signable-setup">
      <div className="flex items-center gap-2">
        <FileSignature size={18} className="text-[var(--primary)]" />
        <span className="font-head text-lg">SaaS Agreement — signable PDF (69 pages)</span>
        {meta?.uploaded && <Badge color="success">{meta.pages} pages</Badge>}
      </div>
      <p className="text-sm opacity-60">
        Upload your exact SaaS Agreement PDF. Each Runner’s typed name, signature and date, plus the GCA specimen
        (<b>{gca.name} — {gca.title}</b>), are overlaid onto <b>your</b> document at signing — preserving your page layout.
        Drag each marker onto the right spot, set its page, then Save.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={upload} data-testid="saas-pdf-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="saas-pdf-upload">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {meta?.uploaded ? "Replace PDF" : "Upload SaaS Agreement PDF"}
        </Button>
        {meta?.uploaded && <>
          <Button variant="subtle" onClick={preview} data-testid="saas-pdf-preview"><Eye size={15} /> Preview executed sample</Button>
          <Button variant="subtle" onClick={savePlacements} disabled={busy} data-testid="saas-pdf-save"><Save size={15} /> Save positions</Button>
          <button onClick={removePdf} className="text-[#DA1E28] hover:opacity-70" data-testid="saas-pdf-remove"><Trash2 size={16} /></button>
        </>}
      </div>

      {meta?.uploaded && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="rounded-lg border border-black/10 dark:border-white/10 p-2 text-sm">
              <span className="opacity-60">GCA name</span>
              <input value={gca.name} onChange={(e) => setGca({ ...gca, name: e.target.value })} data-testid="saas-gca-name"
                className="ms-2 rounded border border-black/15 dark:border-white/15 bg-transparent px-2 py-0.5" />
            </div>
            <div className="rounded-lg border border-black/10 dark:border-white/10 p-2 text-sm">
              <span className="opacity-60">GCA title</span>
              <input value={gca.title} onChange={(e) => setGca({ ...gca, title: e.target.value })} data-testid="saas-gca-title"
                className="ms-2 rounded border border-black/15 dark:border-white/15 bg-transparent px-2 py-0.5" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="opacity-60">Viewing page</span>
            <input type="number" min={1} max={meta.pages} value={viewPage} data-testid="saas-view-page"
              onChange={(e) => setViewPage(Math.max(1, Math.min(meta.pages, parseInt(e.target.value) || 1)))}
              className="w-20 rounded border border-black/15 dark:border-white/15 bg-transparent px-2 py-1" />
            <span className="opacity-50">of {meta.pages}. Markers on this page can be dragged. Coloured pills below jump to a field’s page.</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {Object.keys(labels).map((k) => (
              <button key={k} type="button" data-testid={`saas-field-jump-${k}`}
                onClick={() => setViewPage(placements[k]?.page || 1)}
                className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs"
                style={{ borderColor: FIELD_COLORS[k], color: FIELD_COLORS[k] }}>
                <span className="h-2 w-2 rounded-full" style={{ background: FIELD_COLORS[k] }} /> {labels[k]} · p{placements[k]?.page || "?"}
              </button>
            ))}
          </div>

          <div ref={boxRef} className="relative mx-auto max-w-[720px] select-none rounded-lg border border-black/15 dark:border-white/15 bg-neutral-100 dark:bg-neutral-800" data-testid="saas-page-canvas">
            {imgLoading && <div className="grid h-96 place-items-center opacity-50"><Loader2 className="animate-spin" /></div>}
            {!imgLoading && pageImg && <img src={pageImg} alt={`page ${viewPage}`} className="w-full rounded-lg" draggable={false} />}
            {!imgLoading && pageImg && Object.entries(placements).filter(([, c]) => (c.page || 1) === viewPage).map(([k, c]) => (
              <div key={k} data-testid={`saas-marker-${k}`}
                onMouseDown={(e) => { e.preventDefault(); setDrag(k); }}
                className="absolute cursor-move whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold text-white shadow"
                style={{ left: `${(c.x || 0) * 100}%`, top: `${(c.y || 0) * 100}%`, transform: "translateY(-50%)", background: FIELD_COLORS[k] }}>
                {labels[k]}
              </div>
            ))}
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {Object.keys(labels).map((k) => (
              <div key={k} className="flex items-center gap-2 rounded-lg border border-black/10 dark:border-white/10 px-3 py-2 text-xs">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: FIELD_COLORS[k] }} />
                <span className="flex-1">{labels[k]}</span>
                <label className="opacity-60">page</label>
                <input type="number" min={1} max={meta.pages} value={placements[k]?.page || 1} data-testid={`saas-page-${k}`}
                  onChange={(e) => setField(k, { page: Math.max(1, Math.min(meta.pages, parseInt(e.target.value) || 1)) })}
                  className="w-16 rounded border border-black/15 dark:border-white/15 bg-transparent px-1.5 py-0.5" />
                <label className="opacity-60">size</label>
                <input type="number" min={6} max={48} value={placements[k]?.size || 14} data-testid={`saas-size-${k}`}
                  onChange={(e) => setField(k, { size: parseInt(e.target.value) || 14 })}
                  className="w-16 rounded border border-black/15 dark:border-white/15 bg-transparent px-1.5 py-0.5" />
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
